import React, { useCallback, useEffect, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { api, downloadCsv } from "@/lib/api";
import { Input } from "@/components/ui/input";

const val = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
};

const Diff = ({ before, after }) => {
  const keys = [...new Set([...Object.keys(before || {}), ...Object.keys(after || {})])];
  if (!keys.length) return <span className="text-ink-soft">—</span>;
  return (
    <div className="space-y-0.5">
      {keys.slice(0, 6).map((k) => (
        <div key={k} className="text-[11px]">
          <span className="text-ink-soft">{k}: </span>
          <span className="line-through text-ink-soft/70">{val((before || {})[k])}</span>
          <span className="mx-1">→</span>
          <span className="font-medium">{val((after || {})[k])}</span>
        </div>
      ))}
    </div>
  );
};

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ actors: [], actions: [], entity_types: [], total: 0 });
  const [f, setF] = useState({ actor_email: "", action: "", entity_type: "", date_from: "", date_to: "", q: "" });
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/audit-logs", { params: { ...params, limit: 300 } })
      .then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, [f]);
  useEffect(() => { api.get("/analytics/audit-meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    load();
  }, [load]);

  const reset = () => setF({ actor_email: "", action: "", entity_type: "", date_from: "", date_to: "", q: "" });

  return (
    <div data-testid="admin-audit-log">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Audit Log</h1>
          <p className="mt-2 text-sm text-ink-soft max-w-2xl">
            Semua perubahan penting tercatat di sini: siapa, apa yang diubah, kapan, dan alasannya.
            Total {meta.total} catatan tersimpan.
          </p>
        </div>
        <div className="flex gap-2">
          <button data-testid="audit-export-csv"
            onClick={() => downloadCsv("/audit-logs/export.csv",
              Object.fromEntries(Object.entries(f).filter(([, v]) => v)), "audit-log.csv")
              .then(() => toast.success("CSV audit log diunduh"))
              .catch(() => toast.error("Gagal mengunduh CSV"))}
            className="h-10 px-4 rounded-md border border-black/15 text-sm flex items-center gap-2">
            <Download size={14} /> Unduh CSV
          </button>
          <button data-testid="audit-refresh" onClick={load}
            className="h-10 px-4 rounded-md border border-black/15 text-sm">Muat ulang</button>
        </div>
      </div>

      <div className="mt-5 flex gap-2 flex-wrap">
        <select data-testid="audit-actor-filter" value={f.actor_email} onChange={(e) => setF({ ...f, actor_email: e.target.value })}
          className="h-10 rounded-md border border-input bg-white px-3 text-sm max-w-[220px]">
          <option value="">Semua staf</option>
          {meta.actors.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select data-testid="audit-action-filter" value={f.action} onChange={(e) => setF({ ...f, action: e.target.value })}
          className="h-10 rounded-md border border-input bg-white px-3 text-sm max-w-[220px]">
          <option value="">Semua aksi</option>
          {meta.actions.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select data-testid="audit-entity-filter" value={f.entity_type} onChange={(e) => setF({ ...f, entity_type: e.target.value })}
          className="h-10 rounded-md border border-input bg-white px-3 text-sm">
          <option value="">Semua objek</option>
          {meta.entity_types.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <Input data-testid="audit-date-from" type="date" className="max-w-[160px]" value={f.date_from}
          onChange={(e) => setF({ ...f, date_from: e.target.value })} />
        <Input data-testid="audit-date-to" type="date" className="max-w-[160px]" value={f.date_to}
          onChange={(e) => setF({ ...f, date_to: e.target.value })} />
        <Input data-testid="audit-search" className="max-w-[220px]" placeholder="Cari aksi / email / ID / alasan"
          value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && load()} />
        <button data-testid="audit-search-btn" onClick={load} className="h-10 px-4 rounded-md border border-black/15 text-sm">Cari</button>
        <button data-testid="audit-reset" onClick={reset} className="h-10 px-4 rounded-md text-sm text-ink-soft hover:text-ink">Reset</button>
      </div>

      <div className="mt-5 overflow-x-auto bg-white border border-black/10 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-cream-dark text-left text-[11px] uppercase tracking-widest text-ink-soft">
            <tr><th className="p-3">Waktu (WIB)</th><th className="p-3">Staf</th><th className="p-3">Aksi</th>
              <th className="p-3">Objek</th><th className="p-3">Perubahan</th><th className="p-3">Alasan</th></tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {rows.map((r) => (
              <tr key={r.id} data-testid={`audit-row-${r.id}`} className="align-top hover:bg-cream-dark/40">
                <td className="p-3 whitespace-nowrap text-xs">{r.at_jkt || (r.at || "").slice(0, 16)}</td>
                <td className="p-3 text-xs">{r.actor_email || "sistem"}
                  <div className="text-[10px] text-ink-soft uppercase tracking-widest">{r.actor_role}</div></td>
                <td className="p-3"><span className="text-xs font-medium">{r.action}</span></td>
                <td className="p-3 text-xs">{r.entity_type || "—"}
                  <div className="text-[10px] text-ink-soft break-all">{r.entity_id}</div></td>
                <td className="p-3"><Diff before={r.before} after={r.after} /></td>
                <td className="p-3 text-xs text-ink-soft max-w-[220px]">{r.reason || "—"}</td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} className="p-5 text-ink-soft" data-testid="audit-empty">Tidak ada catatan untuk filter ini.</td></tr>
            )}
            {loading && <tr><td colSpan={6} className="p-5 text-ink-soft">Memuat…</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
